/**
 * Shared tab layout styles for consistent spacing across all tabs
 * Uses Fluent UI tokens with flexbox + gap for best practices
 * Provides the spacious 40px look by combining container padding and gap
 */

import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

export const useTabLayoutStyles = makeStyles({
  /**
   * Main container for tab content
   * Uses flexbox column with gap between major sections
   */
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXL), // 32px gap between sections
  },

  /**
   * Card container for header section
   * Wraps title and toolbar in a card-like container with rounded corners
   */
  headerCard: {
    backgroundColor: tokens.colorNeutralBackground1, // White background (theme-aware)
    ...shorthands.borderRadius('12px'), // Rounded corners
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL), // Comfortable padding
    boxShadow: tokens.shadow4, // Subtle drop shadow for depth
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXL), // Gap between title and toolbar sections
  },

  /**
   * Header section with title and subtitle
   * Optional header at top of page before toolbar
   */
  header: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS), // 8px gap between title/subtitle
  },

  /**
   * Page title styling
   * Large, bold heading for page name
   */
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },

  /**
   * Toolbar/header section containing search, filters, and action buttons
   * Uses flexbox row with gap between controls
   */
  toolbar: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM), // 12px gap between controls
    alignItems: 'center',
  },

  /**
   * Search box consistent width
   */
  searchBox: {
    minWidth: '300px',
  },

  /**
   * Refresh button style for data reloading
   * Consistent placement and appearance across all tabs
   */
  refreshButton: {
    minWidth: 'auto',
  },

  /**
   * Tabs container with reduced bottom spacing
   * Cancels out most of the headerCard's bottom padding for tighter layout
   * Uses flexbox to align tabs on left and action buttons on right
   */
  tabsContainer: {
    marginBottom: `calc(-1 * ${tokens.spacingVerticalXL})`, // Pull tabs very close to bottom
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
